import { getImageProps } from 'next/image';
import type { ImageFileType, ImageType } from '../../../interfaces/components';

// ==================== DATA EXTRACTION ====================

/**
 * Extracts image objects from CMS data.
 * Prioritizes file array, falls back to image_default/image_mobile props.
 */
export const extractImageObjects = (
  file?: ImageFileType[],
  image_default?: ImageType,
  image_mobile?: ImageType,
) => {
  const imageObjDefault =
    file?.find(
      (f) => f.field_name === 'field_image_default' || f.field_name === 'field_svg_file',
    ) ??
    image_default ??
    null;

  const imageObjMobile =
    file?.find((f) => f.field_name === 'field_image_mobile') ?? image_mobile ?? null;

  return { imageObjDefault, imageObjMobile };
};

/**
 * Extracts width and height from image data object.
 * Returns null for both if data is invalid or missing.
 */
export const extractDimensions = (imageObj: ImageType | ImageFileType | null) => {
  if (!imageObj || Array.isArray(imageObj.data)) {
    return { width: null, height: null };
  }
  return {
    width: imageObj.data?.width ? String(imageObj.data.width) : null,
    height: imageObj.data?.height ? String(imageObj.data.height) : null,
  };
};

/**
 * Builds the image source URL, applying optimization transforms if enabled.
 */
export const buildImageSrc = (
  imageObj: ImageType | ImageFileType | null,
  imageOptimizationEnabled: boolean,
  uriTransformSource: RegExp,
  uriTransformEndpoint: string,
) => {
  if (!imageObj?.url) return '';
  if (imageOptimizationEnabled && typeof imageObj?.uri === 'string') {
    return imageObj.uri.replace(uriTransformSource, uriTransformEndpoint);
  }
  return imageObj.url;
};

// ==================== RENDERING ====================

/**
 * Renders a simple fallback image without optimization.
 * Used for SVGs or images without dimensions.
 */
export const renderFallbackImage = (
  imgref: React.RefObject<HTMLImageElement | null>,
  defaultSrc: string,
  altText: string,
  lazyLoading: boolean,
  fieldDisplayVariant: string | undefined,
  borderRadius: string | undefined,
) => (
  <img
    ref={imgref}
    src={defaultSrc}
    alt={altText}
    loading={lazyLoading ? 'lazy' : 'eager'}
    style={{ borderRadius: fieldDisplayVariant === 'circle' ? '50%' : borderRadius }}
  />
);

/**
 * Renders optimized images with responsive sources.
 * Creates a <picture> element if mobile variant exists, otherwise a single <img>.
 */
export const generateAndRenderOptimizedImages = (params: {
  defaultSrc: string;
  defaultWidth: string;
  defaultHeight: string;
  mobileSrc: string | null;
  mobileWidth: string | null;
  mobileHeight: string | null;
  canOptimizeMobile: boolean;
  commonImageProps: {
    alt: string;
    loading?: 'lazy';
    priority?: boolean;
    quality?: number;
    unoptimized?: boolean;
  };
  imageStyle: { objectFit: 'contain'; borderRadius: string | undefined };
  imgref: React.RefObject<HTMLImageElement | null>;
  altText: string;
}) => {
  const {
    defaultSrc,
    defaultWidth,
    defaultHeight,
    mobileSrc,
    mobileWidth,
    mobileHeight,
    canOptimizeMobile,
    commonImageProps,
    imageStyle,
    imgref,
    altText,
  } = params;

  // Generate desktop image props
  const {
    props: { srcSet: desktopSrcSet, ...desktopProps },
  } = getImageProps({
    ...commonImageProps,
    src: defaultSrc,
    width: Number.parseInt(defaultWidth, 10),
    height: Number.parseInt(defaultHeight, 10),
    sizes: canOptimizeMobile ? '(min-width: 601px) 90vw' : '(max-width: 600px) 100vw, 90vw',
  });

  // Generate mobile image props if available
  const mobileImageProps =
    canOptimizeMobile && mobileSrc && mobileWidth && mobileHeight
      ? getImageProps({
          ...commonImageProps,
          src: mobileSrc,
          width: Number.parseInt(mobileWidth, 10),
          height: Number.parseInt(mobileHeight, 10),
          sizes: '(max-width: 600px) 100vw',
        })
      : null;

  // Render with or without mobile variant
  if (mobileImageProps) {
    return (
      <picture>
        <source
          media="(max-width: 600px)"
          srcSet={mobileImageProps.props.srcSet}
          sizes={mobileImageProps.props.sizes}
        />
        <img
          {...desktopProps}
          ref={imgref}
          srcSet={desktopSrcSet}
          style={imageStyle}
          alt={altText}
        />
      </picture>
    );
  }

  return (
    <img {...desktopProps} ref={imgref} srcSet={desktopSrcSet} style={imageStyle} alt={altText} />
  );
};
